import * as React from 'react';
import { map, toLower } from 'lodash';

import { Button, Select, Input } from 'antd';
import { Modal, message } from '@revfluence/fresh';

import { logger } from '@common';
import { useGrantResourceAccessMutation } from '@frontend/app/hooks';

const { Option } = Select;
const { useState, useCallback } = React;

interface IGrantAccessUser {
  id: string;
  name: string;
  email: string;
}

interface IGrantAccessDialogProps {
  resourceId: number;
  clientId: string;
  users: IGrantAccessUser[];
  onGranted: () => void;
}

// Lets a specific user send through this individual (non-shared) mailbox without the
// destructive, all-team isShared toggle. See EPD-10466.
const GrantAccessDialog: React.FC<React.PropsWithChildren<IGrantAccessDialogProps>> = React.memo((props) => {
  const { resourceId, clientId, users, onGranted } = props;

  const [open, setOpen] = useState(false);
  const [targetUserId, setTargetUserId] = useState<string>(undefined);
  const [reason, setReason] = useState<string>('');

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => {
    setOpen(false);
    setTargetUserId(undefined);
    setReason('');
  }, []);

  const [grantResourceAccess, { loading }] = useGrantResourceAccessMutation({
    onCompleted() {
      message.success('Access granted.');
      closeModal();
      onGranted();
    },
    onError(error) {
      logger.error(`Failed to grant resource access for resource ${resourceId}: ${error}`, error);
      message.error('Failed to grant access. Please try refreshing.');
    },
    context: {
      headers: {
        'requested-client-id': clientId,
      },
    },
  });

  const onSubmit = useCallback(() => {
    grantResourceAccess({
      variables: {
        resourceId,
        targetUserId,
        reason: reason || undefined,
      },
    });
  }, [grantResourceAccess, resourceId, targetUserId, reason]);

  return (
    <>
      <Button onClick={openModal}>Grant access</Button>
      <Modal
        title="Grant mailbox access"
        okText="Grant access"
        cancelText="Cancel"
        open={open}
        confirmLoading={loading}
        okButtonProps={{ disabled: !targetUserId }}
        onOk={onSubmit}
        onCancel={closeModal}
      >
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          placeholder="Who needs access?"
          value={targetUserId}
          onChange={(id: string) => setTargetUserId(id)}
          filterOption={(input, option) => toLower(option?.children as unknown as string).includes(toLower(input))}
          showSearch
        >
          {map(users, (user) => (
            <Option key={user.id} value={user.id}>
              {`${user.name} <${user.email}>`}
            </Option>
          ))}
        </Select>
        <Input
          placeholder="Reason (e.g. incident/ticket reference)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
    </>
  );
});
GrantAccessDialog.displayName = 'GrantAccessDialog';

export default GrantAccessDialog;
